import { useEffect, useState } from 'react'

import { formatToPTBRNumber } from 'commons/utils/number'
import { convertBooleanApiValue } from 'commons/utils/string'
import { getUser } from 'commons/utils/user'
import {
  Icon,
  InternationalSampleHeader,
  LoadingModal,
  NationalSampleHeader,
  type OptionProps,
  OVOrderHeader,
  Page,
  StatisticsCard,
  ToolBar,
} from 'components'
import {
  Company,
  defaultOvTypeOptions,
  MaterialType,
  Organization,
  OVOrder,
  SampleOrderCostElement,
  TypesValidations,
} from 'constants/enums'
import { URL_CONST } from 'constants/urls'
import type { PaymentMethodsProps } from 'container/CreateOrder/components/TopSection'
import type { CancelOvReason } from 'container/SalesOrder/components/OvsTab/components/CancelOvForm'
import { FormProvider, useForm } from 'react-hook-form'
import { useMatch, useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { getPaymentsTerms } from 'services/draft'
import { oVMaintenance } from 'services/manage_ov'
import type { OvMaintenanceResponse } from 'services/manage_ov/interface'
import { getClientAddress } from 'services/salesOrder'
import type { AddressResponse } from 'services/salesOrder/types'
import { asyncGetOVById } from 'store/OVContext'
import type {
  OVDetalheResponse,
  OvMaintenanceBody,
  SapOptionsToOvStatus,
} from 'store/OVContext/ovContextTypes'
import { asyncSetOvSap, getOvBinds, setOVSelected } from 'store/OVContext/ovSlice'
import {
  SalesOrderOvCancelOperation,
  SalesOrderOvDeleteOperation,
} from 'store/SalesOrderContext/salesOrderSlice'
import { setOvInitialTab } from 'store/StructuralContext/structuralSlice'
import { useDispatch, useSelector } from 'store/store'

import OvDetailsTabs from './components/OvDetailsTabs'
import * as S from './styles'
import { getOvStatusOptionsToSap } from './utils'

export default function ManageOVDetails() {
  const { ovNumber } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const matchJustifyRoute = !!useMatch(`${URL_CONST.MANAGE_OV}/:ovNumber/justificar`)

  const {
    ovSelected,
    loadingOv,
    savingOv,
    detailedInfo: { FaturamentoEntrega },
    detailedInfo,
    observation,
    alertJustification,
  } = useSelector(({ ov }) => ov)
  const { isYPOP, isYASC, isSampleOrder } = useSelector(({ salesOrder }) => salesOrder)

  const pepElement = ovSelected?.Cabecalho?.CodigoElementoPep?.trim()
  const costCenter = ovSelected?.Cabecalho?.CodigoCentroCusto?.trim()

  const dataToHeader = {
    cd_canl_dstb: ovSelected?.Cabecalho?.CanalDistribuicao?.substring(0, 2) ?? '',
    cd_catg_item: ovSelected?.Cabecalho?.CodigoCategoriaItem ?? '',
    cd_cent_cust: costCenter ?? '',
    cd_cent_matr: ovSelected?.Cabecalho?.CodigoCentroMaterial ?? '',
    cd_clie_entr: ovSelected?.Cabecalho?.EnderecoEntrega ?? '',
    cd_cond_pgto: ovSelected?.Cabecalho?.CondicaoPagamento?.substring(0, 4) ?? '',
    cd_elem_pep: pepElement ?? '',
    cd_escr_vend: ovSelected?.Cabecalho?.CodigoEscritorioVenda ?? '',
    cd_inco: ovSelected?.Cabecalho?.Incoterm ?? '',
    cd_mes_fatm: ovSelected?.Cabecalho?.MesFaturamento ?? '',
    cd_mes_pedi: ovSelected?.Cabecalho?.MesPedido ?? '',
    cd_moti_prom: ovSelected?.Cabecalho?.CodigoMotivoPromocional ?? '',
    cd_orgn_vend: Organization.ALHV,
    cd_seto_atvd: ovSelected?.Cabecalho?.Setor?.substring(0, 2) ?? '',
    cd_tipo_orde: ovSelected?.Cabecalho?.CodigoTipoOrdem ?? '',
    ds_armz: ovSelected?.Cabecalho?.DestinationWarehouse ?? '',
    ds_grup_clie: ovSelected?.Cabecalho?.SubCanalDistribuicao ?? '',
    ds_text_nota_fisc: ' ',
    dt_ano_mes_cota: ovSelected?.Cabecalho?.MesCota ?? '',
    dt_dsjd_pedi: '',
    fl_cbtr_camb: ovSelected?.Cabecalho?.CoberturaCambial ?? TypesValidations.NO,
    fl_data_fixa_entr: ovSelected?.Cabecalho?.FlagDataFixaEntrega ?? TypesValidations.NO,
    fl_mto_prep:
      ovSelected?.Cabecalho?.TipoMaterial === MaterialType.MTO
        ? TypesValidations.YES
        : TypesValidations.NO,
    fl_prmt_desd: TypesValidations.YES,
    nr_dias_adic: ovSelected?.Cabecalho?.PrazoExtra?.toString() ?? '0',
    nr_insc_estl: ovSelected?.Cabecalho?.InscricaoEstadual ?? '',
    nr_pedi_clie: ovSelected?.Cabecalho?.NumeroPedidoCliente ?? ' ',
    op_elem_cust:
      (pepElement && SampleOrderCostElement.PEP) ||
      (costCenter && SampleOrderCostElement.COST) ||
      '',
    pc_dsct: ovSelected?.Cabecalho?.Desconto ?? 0,
  }
  const initialFields = {
    ...dataToHeader,
  }

  const defaultClient = {
    cd_canl_dstb: ovSelected?.Cabecalho?.CanalDistribuicao?.substring(0, 2),
    cd_clie: ovSelected?.Cabecalho?.CodigoSapCliente,
    cd_empr: Company.ALPA,
    cd_escr_vend: ovSelected?.Cabecalho?.CodigoEscritorioVenda,
    cd_orgn_vend: Organization.ALHV,
    cd_regi_vend: ovSelected?.Cabecalho?.RegiaoVenda.substring(0, 6),
    cd_seto_atvd: ovSelected?.Cabecalho?.Setor.substring(0, 2),
    ds_regi_vend: ovSelected?.Cabecalho?.RegiaoVenda?.substring(8)?.trim(),
    nm_clie: ovSelected?.Cabecalho?.RazaoSocial,
    nr_cnpj_cpf: ovSelected?.Cabecalho?.CnpjCpf,
  }
  const methods = useForm({
    defaultValues: initialFields,
    mode: 'all',
    reValidateMode: 'onBlur',
  })

  useEffect(() => {
    if (Object.keys(ovSelected).length) {
      const pepElement = ovSelected?.Cabecalho?.CodigoElementoPep?.trim()
      const costCenter = ovSelected?.Cabecalho?.CodigoCentroCusto?.trim()

      methods.reset({
        cd_canl_dstb: ovSelected?.Cabecalho?.CanalDistribuicao?.substring(0, 2) ?? '',
        cd_catg_item: ovSelected?.Cabecalho?.CodigoCategoriaItem ?? '',
        cd_cent_cust: costCenter ?? '',
        cd_cent_matr: ovSelected?.Cabecalho?.CodigoCentroMaterial ?? '',
        cd_clie_entr: ovSelected?.Cabecalho?.EnderecoEntrega ?? '',
        cd_cond_pgto: ovSelected?.Cabecalho?.CondicaoPagamento?.substring(0, 4) ?? '',
        cd_elem_pep: pepElement ?? '',
        cd_escr_vend: ovSelected?.Cabecalho?.CodigoEscritorioVenda ?? '',
        cd_inco: ovSelected?.Cabecalho?.Incoterm ?? '',
        cd_mes_fatm: ovSelected?.Cabecalho?.MesFaturamento ?? '',
        cd_mes_pedi: ovSelected?.Cabecalho?.MesPedido ?? '',
        cd_moti_prom: ovSelected?.Cabecalho?.CodigoMotivoPromocional ?? '',
        cd_orgn_vend: Organization.ALHV,
        cd_seto_atvd: ovSelected?.Cabecalho?.Setor?.substring(0, 2) ?? '',
        cd_tipo_orde: ovSelected?.Cabecalho?.CodigoTipoOrdem ?? '',
        ds_armz: ovSelected?.Cabecalho?.DestinationWarehouse ?? '',
        ds_grup_clie: ovSelected?.Cabecalho?.SubCanalDistribuicao ?? '',
        ds_text_nota_fisc: ' ',
        dt_ano_mes_cota: ovSelected?.Cabecalho?.MesCota || '',
        dt_dsjd_pedi: '',
        fl_cbtr_camb: ovSelected?.Cabecalho?.CoberturaCambial ?? TypesValidations.NO,
        fl_data_fixa_entr: ovSelected?.Cabecalho?.FlagDataFixaEntrega ?? TypesValidations.NO,
        fl_mto_prep:
          ovSelected?.Cabecalho?.TipoMaterial === MaterialType.MTO
            ? TypesValidations.YES
            : TypesValidations.NO,
        fl_prmt_desd: TypesValidations.YES,
        nr_dias_adic: ovSelected?.Cabecalho?.PrazoExtra?.toString() ?? '0',
        nr_insc_estl: ovSelected?.Cabecalho?.InscricaoEstadual ?? '',
        nr_pedi_clie: ovSelected?.Cabecalho?.NumeroPedidoCliente ?? ' ',
        op_elem_cust:
          (pepElement && SampleOrderCostElement.PEP) ||
          (costCenter && SampleOrderCostElement.COST) ||
          '',
        pc_dsct: ovSelected?.Cabecalho?.Desconto ?? 0,
      })
      setInputAutocomplete(ovSelected?.Cabecalho?.CondicaoPagamento)
      getAddress()
    }
  }, [JSON.stringify(ovSelected)])

  useEffect(() => {
    if (FaturamentoEntrega?.DataDesejadaCliente) {
      methods.setValue('dt_dsjd_pedi', FaturamentoEntrega.DataDesejadaCliente)
    }
  }, [FaturamentoEntrega])

  useEffect(() => {
    if (observation?.TextosObservacao?.length) {
      methods.setValue('ds_text_nota_fisc', observation?.TextosObservacao[0]?.DescricaoTexto ?? ' ')
    }
  }, [observation])

  useEffect(() => {
    if (alertJustification || matchJustifyRoute) {
      dispatch(setOvInitialTab(2))

      if (ovNumber) {
        dispatch(asyncGetOVById(ovNumber))
      }
    }
  }, [alertJustification, matchJustifyRoute])

  const [ovsSapData, setOvsSapData] = useState<SapOptionsToOvStatus>({} as SapOptionsToOvStatus)
  const [inputAutocomplete, setInputAutocomplete] = useState<string>('')
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethodsProps[]>([])
  const [addressOptions, setAddressOptions] = useState<OptionProps[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const handleOvToSap = () => {
    dispatch(
      asyncSetOvSap({
        body: ovsSapData.body,
        reload: !(observation?.NegociacaoDiferenciada === TypesValidations.YES),
        type: ovsSapData.type,
      }),
    )
  }

  const getCondicoesPagamento = async () => {
    const returnApi = await getPaymentsTerms({
      cd_canl_dstb: '',
      cd_cond_pgto: '',
      cd_empr: '',
      cd_orgn_vend: '',
      cd_tipo_orde: '',
      fl_orde_tele_vend: TypesValidations.NO,
      qt_rowcount: 999,
    })
    if (returnApi) {
      localStorage.setItem('paymentMethods', JSON.stringify(returnApi))
      setPaymentMethods(returnApi)
    }
  }

  const getAddress = () =>
    getClientAddress({
      cd_canl_dstb: defaultClient.cd_canl_dstb,
      cd_clie: defaultClient.cd_clie,
      cd_empr: defaultClient.cd_empr,
      cd_orgn_vend: defaultClient.cd_orgn_vend,
      cd_seto_atvd: defaultClient.cd_seto_atvd,
    }).then((response: AddressResponse[]) => {
      setAddressOptions(
        response.map((address) => ({
          title: address.ds_endr,
          value: address.cd_pcro,
        })),
      )
      if (response.length === 1) {
        methods.setValue('cd_clie_entr', response[0].cd_pcro)
      }
      if (response.length > 1) {
        methods.setValue(
          'cd_clie_entr',
          response?.find((address) => address.ds_endr === ovSelected?.Cabecalho?.EnderecoEntrega)
            ?.cd_pcro ?? '',
        )
      }
    })

  useEffect(() => {
    getCondicoesPagamento()
    if (ovNumber) {
      dispatch(asyncGetOVById(ovNumber))
      dispatch(getOvBinds(ovNumber))
    }
    return () => {
      dispatch(setOVSelected({} as OVDetalheResponse))
    }
  }, [])
  const editOv = async (ovID: string, requestBody: OvMaintenanceBody) => {
    try {
      setLoading(true)
      const res: OvMaintenanceResponse = await oVMaintenance(ovID, requestBody)
      if (res.OperacaoRealizada) {
        toast.success('Ordem de Venda editada com sucesso.')
        methods.reset({ ...methods.getValues() })
      } else {
        toast.error(res?.Mensagem || `Não foi possível editar a Ordem de Venda.`)
      }
    } catch (error) {
      toast.error(`Erro ao editar a Ordem de Venda.`)
    } finally {
      setLoading(false)
    }
  }
  const handleEditOv = (data: typeof initialFields) => {
    const editOvRequestBody = {
      CodigoEntrega: data.cd_clie_entr,
      CodigoUsuarioAlteracao: getUser(),
      CondicaoPagamento: data.cd_cond_pgto,
      DataDesejadaCliente: FaturamentoEntrega.DataDesejadaCliente,
      DataPrevistaFaturamento: FaturamentoEntrega.DataPrevisaoFaturamento,
      DiasAdicionais: +data.nr_dias_adic,
      NumeroOrdemVenda: ovNumber,
      NumeroPedidoCliente: data.nr_pedi_clie,
      TipoOrdem: data.cd_tipo_orde,
    }
    if (ovNumber) {
      editOv(ovNumber, editOvRequestBody as OvMaintenanceBody)
    }
  }

  const quotasMonth = ovSelected?.Cabecalho?.MesCota
    ? [
        {
          ds_ano_mes_cota: ovSelected?.Cabecalho?.MesCota?.replace(/([\d]{4})([\d]{2})/, '$2/$1'),
          dt_ano_mes_cota: ovSelected?.Cabecalho?.MesCota,
          fl_ano_mes_cota_antp: TypesValidations.NO,
        },
      ]
    : []
  useEffect(() => {
    if (ovSelected.statusOrdemVenda && detailedInfo?.Tracking?.DataUltimaAlteracao && ovNumber) {
      const { statusOrdemVenda } = ovSelected
      setOvsSapData(
        getOvStatusOptionsToSap([
          {
            changeDate: detailedInfo?.Tracking?.DataUltimaAlteracao,
            number: ovNumber,
            statusKey: statusOrdemVenda,
          },
        ]),
      )
    }
  }, [ovSelected, detailedInfo?.Tracking?.DataUltimaAlteracao])

  const dispatchCancelOperation = (data: CancelOvReason) => {
    const body = {
      codigoMotivo: data.reasonsRefusal,
      justificativa: data.refusalDescription,
      ordensVenda: [
        {
          dataUltimaAlteracao: detailedInfo?.Tracking?.DataUltimaAlteracao,
          numeroOrdem: ovNumber as string,
        },
      ],
    }
    dispatch(
      SalesOrderOvCancelOperation(
        body,
        { linhas: 1, paginas: 0 },
        ovSelected?.Cabecalho?.NumeroPedidoRascunho ?? '',
      ),
    )
  }

  const dispatchDeleteOperation = () => {
    dispatch(
      SalesOrderOvDeleteOperation(
        [ovNumber as string],
        { linhas: 1, paginas: 0 },
        ovSelected?.Cabecalho?.NumeroPedidoRascunho ?? '',
      ),
    )
    if (ovSelected?.Cabecalho?.NumeroPedidoRascunho) {
      navigate(`${URL_CONST.MANAGE_ORDER}/${ovSelected?.Cabecalho?.NumeroPedidoRascunho}`, {
        replace: true,
      })
    } else {
      navigate(`${URL_CONST.MANAGE}`, { replace: true })
    }
  }

  const renderSamplaOrderHeader = () => {
    if (isYPOP) {
      return (
        <NationalSampleHeader defaultCustomer={defaultClient} defaultPayment={inputAutocomplete} />
      )
    }

    if (isYASC) {
      return (
        <InternationalSampleHeader
          defaultCustomer={defaultClient}
          defaultPayment={inputAutocomplete}
        />
      )
    }

    return null
  }

  return loadingOv ? (
    <LoadingModal />
  ) : (
    <>
      <Page
        fullwidth
        renderHeaderComponent={
          <ToolBar
            fullWidth
            isButtonCancel={
              +ovSelected?.Cabecalho?.CodigoStatus >= 0 && +ovSelected?.Cabecalho?.CodigoStatus <= 8
            }
            isButtonDelete={
              +ovSelected?.Cabecalho?.CodigoStatus === 0 &&
              !ovSelected?.Cabecalho?.NumeroOrdemVendaSap
            }
            isButtonTrack
            isLoadingButtonSave={savingOv}
            isND={observation?.NegociacaoDiferenciada === TypesValidations.YES}
            onClickCancel={dispatchCancelOperation}
            onClickDelete={dispatchDeleteOperation}
            onClickSap={handleOvToSap}
            orderStatus="ordered"
            originOrder={ovSelected?.Cabecalho?.OrigemPedido}
            ovPortalNumber={ovNumber}
            ovSapNumber={ovSelected?.Cabecalho?.NumeroOrdemVendaSap}
            sapButtonsOptions={ovsSapData}
            showButtonMore={true}
          />
        }
      >
        <FormProvider {...methods}>
          <>
            {isSampleOrder ? (
              renderSamplaOrderHeader()
            ) : (
              <OVOrderHeader
                addressOptions={addressOptions}
                clientOvTypes={defaultOvTypeOptions}
                defaultClient={defaultClient}
                defaultExpanded={open}
                inputAutocomplete={inputAutocomplete}
                inputAutocompleteSaleRegion={ovSelected?.Cabecalho?.RegiaoVenda ?? ''}
                loading={loading}
                paymentMethods={paymentMethods}
                postDraft={async () => {
                  setOpen(true)
                  handleEditOv(methods.getValues())
                }}
                quotasMonth={quotasMonth}
                setAddressOptions={setAddressOptions}
                setInputAutocomplete={setInputAutocomplete}
                type={OVOrder.OV}
              />
            )}
            <S.StatisticsRow>
              <StatisticsCard
                icon={<Icon color="#94A3BC" icon="sandal" size="25" />}
                mainValue={ovSelected?.Faturamento?.QuantidadeTotalOV?.toString()}
                title="Quantidade total OV"
              >
                <S.InnerRow>
                  <S.Box>
                    <S.BoxTitle>Total faturado</S.BoxTitle>
                    <S.BoxValue>{ovSelected?.Faturamento?.QuantidadeTotalFaturada}</S.BoxValue>
                  </S.Box>
                  <S.Box>
                    <S.BoxTitle>Saldo p/ faturar</S.BoxTitle>
                    <S.BoxValue>{ovSelected?.Faturamento?.QuantidadeSaldoFaturar}</S.BoxValue>
                  </S.Box>
                  <S.Box>
                    <S.BoxTitle>Total cancelado</S.BoxTitle>
                    <S.BoxValue>{ovSelected?.Faturamento?.QuantidadeTotalCanceladoOV}</S.BoxValue>
                  </S.Box>
                </S.InnerRow>
              </StatisticsCard>
              <StatisticsCard
                icon={<Icon color="#94A3BC" icon="money" size="25" />}
                mainValue={formatToPTBRNumber(
                  ovSelected?.Faturamento?.ValorReferenciaOV ?? 0,
                  false,
                )}
                title="Valor de referência OV"
              >
                <S.InnerRow>
                  <S.Box>
                    <S.BoxTitle>Total SAP</S.BoxTitle>
                    <S.BoxValue>
                      {formatToPTBRNumber(ovSelected?.Faturamento?.ValorTotalSAP ?? 0)}
                    </S.BoxValue>
                  </S.Box>
                  <S.Box>
                    <S.BoxTitle>Diferença entre portal/SAP</S.BoxTitle>
                    <S.BoxValue>
                      {formatToPTBRNumber(ovSelected?.Faturamento?.ValorDiferencaPortalSap ?? 0)}
                    </S.BoxValue>
                  </S.Box>
                  <S.Box>
                    <S.BoxTitle>Total faturado</S.BoxTitle>
                    <S.BoxValue>
                      {formatToPTBRNumber(ovSelected?.Faturamento?.ValorTotalFaturado ?? 0)}
                    </S.BoxValue>
                  </S.Box>
                </S.InnerRow>
              </StatisticsCard>
            </S.StatisticsRow>
            <OvDetailsTabs
              hasShipment={
                [65, 70].includes(+ovSelected?.Cabecalho?.CodigoStatus) ||
                convertBooleanApiValue(ovSelected?.Cabecalho?.FlagRemessa)
              }
              isBilled={
                [75, 80].includes(+ovSelected?.Cabecalho?.CodigoStatus) ||
                convertBooleanApiValue(ovSelected?.Cabecalho?.FlagFatura)
              }
            />
          </>
        </FormProvider>
      </Page>
    </>
  )
}
